/**
 * Экран рейтинга (ТЗ FR-LB-01, FR-LB-05 … FR-LB-07).
 *
 * Таблица была только на сервере: он считал её с самого начала, а показать её
 * человеку было нечем — согласие на участие лежало в браузере и никуда не
 * уходило. Обещание «участвовать в рейтинге» не исполнялось ни при каких
 * условиях.
 *
 * Экран говорит правду о трёх вещах сразу: участвуете ли вы (по ответу
 * сервера, а не по галочке в этом браузере), из чего считается место
 * (засчитанные контрольные результаты) и когда снимок посчитан — таблица,
 * молчащая о своей давности, читается как живая.
 *
 * Видов рейтинга пять, и они независимы (ТЗ FR-LB-01). Выбор вида здесь
 * потому, что без него человек видел бы один Progress: сервер считает все
 * пять, а спросить было можно только один.
 */

import { useState } from 'react';
import type { Translate } from '@type2x/i18n';
import { LEADERBOARD_KINDS } from '@type2x/feature-leaderboard';
import type { LeaderboardKind } from '@type2x/feature-leaderboard';
import { Button, Card, ChoiceGroup, EmptyState } from '@type2x/ui';

import type { LeaderboardAccess } from '../state/leaderboard.js';
import type { FriendsAccess } from '../state/friends.js';
import { BoardTable } from './BoardTable.js';
import { FriendsBlock } from './FriendsBlock.js';

export interface LeaderboardScreenProps {
  readonly t: Translate;
  readonly leaderboard: LeaderboardAccess;
  readonly friends: FriendsAccess;
  /** Код приглашения из ссылки, если приложение открыли по ней. */
  readonly invite: string | null;
  /** Куда уйти за аккаунтом: без него рейтинга нет. */
  readonly onSaveProgress: () => void;
  readonly onBack: () => void;
}

export function LeaderboardScreen({
  t,
  leaderboard,
  friends,
  invite,
  onSaveProgress,
  onBack,
}: LeaderboardScreenProps): React.JSX.Element {
  const { state } = leaderboard;

  return (
    <Card title={t('leaderboard.title')}>
      <p className="t2x-muted">{t('leaderboard.explain')}</p>

      {/* Выбор вида остаётся на месте и во время запроса: иначе сменить его
          второй раз было бы нечем, пока не приедет первый. */}
      {state.status !== 'unavailable' && <KindChoice t={t} leaderboard={leaderboard} />}

      {state.status === 'loading' && <p className="t2x-muted">{t('leaderboard.loading')}</p>}

      {state.status === 'unavailable' && (
        <EmptyState
          title={t('leaderboard.unavailableTitle')}
          body={t('leaderboard.unavailable')}
          action={
            <Button variant="secondary" onClick={onSaveProgress}>
              {t('account.title')}
            </Button>
          }
        />
      )}

      {state.status === 'ready' && (
        <Board t={t} leaderboard={leaderboard} friends={friends} invite={invite} />
      )}

      <Button variant="ghost" onClick={onBack}>
        {t('report.back')}
      </Button>
    </Card>
  );
}

/**
 * Выбор вида рейтинга и что он означает.
 *
 * Название вида без объяснения не говорит ничего: «Consistency» не сообщает,
 * что считается регулярностью. Порядок таблицы обязан быть объясним, иначе
 * он читается как случайный.
 */
function KindChoice({
  t,
  leaderboard,
}: {
  readonly t: Translate;
  readonly leaderboard: LeaderboardAccess;
}): React.JSX.Element {
  return (
    <section className="t2x-leaderboard__kinds">
      <ChoiceGroup<LeaderboardKind>
        legend={t('leaderboard.kinds')}
        name="leaderboard-kind"
        value={leaderboard.kind}
        onChange={leaderboard.selectKind}
        options={LEADERBOARD_KINDS.map((kind) => ({
          value: kind,
          label: t(`leaderboard.kind.${kind}`),
        }))}
      />
      <p className="t2x-muted">{t(`leaderboard.hint.${leaderboard.kind}`)}</p>
    </section>
  );
}

/** Таблица и управление участием — то, что показывается по ответу сервера. */
function Board({
  t,
  leaderboard,
  friends,
  invite,
}: {
  readonly t: Translate;
  readonly leaderboard: LeaderboardAccess;
  readonly friends: FriendsAccess;
  readonly invite: string | null;
}): React.JSX.Element | null {
  if (leaderboard.state.status !== 'ready') return null;
  const { board } = leaderboard.state;

  return (
    <>
      <Participation t={t} leaderboard={leaderboard} />

      {board.rows.length === 0 ? (
        <EmptyState title={t('leaderboard.emptyTitle')} body={t('leaderboard.empty')} />
      ) : (
        <BoardTable t={t} board={board} />
      )}

      {/* Друзья — тот же рейтинг в кругу знакомых (ТЗ FR-LB-01). Отдельной
          вкладки не нужно: это продолжение того же разговора. */}
      <FriendsBlock t={t} friends={friends} invite={invite} />
    </>
  );
}

/**
 * Участие и псевдоним.
 *
 * Показывается состояние, которое знает сервер, а не галочка этого браузера:
 * участие — договор с сервером, и хранить его на устройстве бессмысленно.
 */
function Participation({
  t,
  leaderboard,
}: {
  readonly t: Translate;
  readonly leaderboard: LeaderboardAccess;
}): React.JSX.Element | null {
  const [name, setName] = useState('');
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (leaderboard.state.status !== 'ready') return null;
  const { participating } = leaderboard.state.board;

  async function apply(options: { optIn?: boolean; displayName?: string }): Promise<void> {
    setBusy(true);
    setFailed(!(await leaderboard.save(options)));
    setBusy(false);
  }

  return (
    <section className="t2x-leaderboard__participation">
      <label className="t2x-check">
        <input
          type="checkbox"
          checked={participating}
          disabled={busy}
          onChange={(event) => void apply({ optIn: event.target.checked })}
        />
        <span>{t('leaderboard.consent')}</span>
      </label>
      <p className="t2x-muted">{t('leaderboard.consentHint')}</p>

      <label className="t2x-field">
        <span>{t('leaderboard.name')}</span>
        <input
          type="text"
          maxLength={64}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <Button
        variant="secondary"
        disabled={busy || name.trim() === ''}
        onClick={() => void apply({ displayName: name.trim() })}
      >
        {t('leaderboard.saveName')}
      </Button>

      {failed && (
        <p role="alert" className="t2x-error">
          {t('leaderboard.saveFailed')}
        </p>
      )}
    </section>
  );
}
